import { Button, Table } from 'pink-lava-ui';
import { HistoryIcon } from 'lucide-react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import ResponsiveModal from '@/components/ui/modal/ResponsiveModal';
import { Pagination } from 'antd';
import EmptyPlaceholder from '@/components/ui/EmptyPlaceholder';
import { useChangeLogs } from '@/queries/common.query';

const ChangeLogsButton = ({ url, queryKey }: { url: string; queryKey?: string[] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { isMobile } = useBreakpoint();

  console.log({ page, limit });
  const { data: response, isLoading } = useChangeLogs(
    url,
    {
      page: page,
      limit: limit,
    },
    { queryKey: queryKey }
  );

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setLimit(10);
    }
  }, [isOpen]);

  const changeLogList = response?.data.items;

  const meta = response?.data?.meta;

  return (
    <>
      <Button type="secondary" shape={isMobile ? 'circle' : 'round'} onClick={() => setIsOpen(true)}>
        <HistoryIcon size={isMobile ? 24 : 20} />
        {!isMobile && 'Change Logs'}
      </Button>
      <ResponsiveModal
        title={'Change Logs'}
        open={isOpen}
        centered
        width={706}
        onCancel={() => setIsOpen(false)}
        footer={<></>}
        fullscreenMaxBreakpoint="md"
      >
        <div className="hidden md:block overflow-auto max-h-[calc(100dvh-140px)]">
          <Table
            loading={isLoading}
            size="small"
            dataSource={changeLogList}
            rowKey={'ulid'}
            scroll={{
              x: 640,
            }}
            pagination={{
              page,
              itemsPerPage: limit,
              totalItems: response?.data.meta.total,
              totalPages: Math.ceil((response?.data.meta.total || 0) / limit!),
              perPageOptions: [
                {
                  label: '10',
                  value: 10,
                },
                {
                  label: '20',
                  value: 20,
                },
                {
                  label: '50',
                  value: 50,
                },
                {
                  label: '100',
                  value: 100,
                },
              ],
              setItemsPerPage(itemsPerPage) {
                setLimit(itemsPerPage);
                setPage(1);
              },
              onChange: (page: number) => {
                setPage(page);
              },
            }}
            columns={[
              {
                title: 'Date & Time',
                dataIndex: 'created_at',
                render: (value: string) => dayjs(value).format('DD-MM-YYYY, HH:mm:ss'),
                width: 160,
              },
              {
                title: 'User',
                dataIndex: 'user_name',
              },
              {
                title: 'Tipe',
                dataIndex: 'change_type',
                render: (_: string, record) => (
                  <span>{(record.change_type || record.action || '-').replace('_', ' ')}</span>
                ),
              },
              {
                title: 'Change Log',
                dataIndex: 'log',
              },
            ]}
          />
        </div>
        <div className="flex md:hidden space-y-2 flex-col overflow-auto p-4">
          {!changeLogList || changeLogList.length === 0 ? (
            <EmptyPlaceholder title="Change Log" />
          ) : (
            <>
              {changeLogList?.map(item => (
                <div key={item.ulid} className="border border-gray-400 rounded-xl last:mb-2">
                  <div className="flex flex-row items-center #FAFAFB rounded-tl-xl rounded-tr-xl justify-evenly p-2 border-b border-gray-400 bg-gray-100">
                    <div className="text-sm text-center flex-1">
                      {dayjs(item.created_at).format('DD-MM-YYYY, HH:mm:ss')}
                    </div>
                    <div className="bg-gray-400 h-[32px] w-[1px] mx-1" />
                    <div className="text-sm text-center flex-1">{item.user_name}</div>
                    <div className="bg-gray-400 h-[32px] w-[1px] mx-1" />
                    <div className="text-sm text-center flex-1">
                      {(item.change_type || item.action || '-').replace('_', ' ')}
                    </div>
                  </div>
                  <div className="p-3">{item.log}</div>
                </div>
              ))}
              <Pagination
                total={meta?.total}
                onChange={(page: number, pageSize) => {
                  setPage(page);
                  setLimit(pageSize);
                }}
                pageSize={limit}
                defaultCurrent={page}
                showSizeChanger
                align="center"
              />
            </>
          )}
        </div>
      </ResponsiveModal>
    </>
  );
};

export default ChangeLogsButton;
